import React from 'react'

interface GenderIconProps {
  gender: 'boy' | 'girl'
  size?: number
  animated?: boolean
  className?: string
}

const GenderIcon: React.FC<GenderIconProps> = ({ 
  gender, 
  size = 80, 
  animated = true, 
  className = '' 
}) => {
  const color = gender === 'boy' ? '#3b82f6' : '#ec4899'
  const secondaryColor = gender === 'boy' ? '#60a5fa' : '#f9a8d4'
  
  if (gender === 'boy') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="boyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={secondaryColor} />
          </linearGradient>
        </defs>
        
        {/* Baby Face */}
        <circle cx="50" cy="45" r="25" fill="url(#boyGradient)" stroke="#1e40af" strokeWidth="2">
          {animated && (
            <animate attributeName="r" values="25;27;25" dur="2s" repeatCount="indefinite" />
          )}
        </circle>
        
        {/* Eyes */}
        <circle cx="42" cy="40" r="3" fill="#fff" />
        <circle cx="58" cy="40" r="3" fill="#fff" />
        <circle cx="42" cy="40" r="1.5" fill="#1e40af" />
        <circle cx="58" cy="40" r="1.5" fill="#1e40af" />
        
        {/* Smile */}
        <path d="M 40 50 Q 50 58 60 50" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        
        {/* Baby Cap */}
        <path d="M 25 35 Q 25 20 50 20 Q 75 20 75 35 Q 70 25 50 25 Q 30 25 25 35" 
              fill={color} stroke="#1e40af" strokeWidth="1" />
        
        {/* Pom Pom */}
        <circle cx="50" cy="20" r="4" fill={secondaryColor}>
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 50 20;10 50 20;-10 50 20;0 50 20"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </circle>
        
        {/* Toy Truck */}
        <rect x="30" y="75" width="40" height="15" rx="3" fill={color} />
        <circle cx="38" cy="87" r="5" fill="#1e40af" />
        <circle cx="62" cy="87" r="5" fill="#1e40af" />
        <circle cx="38" cy="87" r="3" fill="#60a5fa" />
        <circle cx="62" cy="87" r="3" fill="#60a5fa" />
        
        {animated && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0;5 0;0 0"
            dur="2s"
            repeatCount="indefinite"
            begin="1s"
          />
        )}
      </svg>
    )
  }
  
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="girlGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={secondaryColor} />
        </linearGradient>
      </defs>
      
      {/* Baby Face */}
      <circle cx="50" cy="45" r="25" fill="url(#girlGradient)" stroke="#be185d" strokeWidth="2">
        {animated && (
          <animate attributeName="r" values="25;27;25" dur="2s" repeatCount="indefinite" />
        )}
      </circle>
      
      {/* Eyes */}
      <circle cx="42" cy="40" r="3" fill="#fff" />
      <circle cx="58" cy="40" r="3" fill="#fff" />
      <circle cx="42" cy="40" r="1.5" fill="#be185d" />
      <circle cx="58" cy="40" r="1.5" fill="#be185d" />
      
      {/* Eyelashes */}
      <path d="M 39 35 L 38 33 M 42 34 L 42 32 M 45 35 L 46 33" 
            stroke="#be185d" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 61 35 L 62 33 M 58 34 L 58 32 M 55 35 L 54 33" 
            stroke="#be185d" strokeWidth="1.5" strokeLinecap="round" />
      
      {/* Smile */}
      <path d="M 40 50 Q 50 58 60 50" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
      
      {/* Hair Bow */}
      <ellipse cx="35" cy="25" rx="8" ry="4" fill={color} transform="rotate(-30 35 25)" />
      <ellipse cx="65" cy="25" rx="8" ry="4" fill={color} transform="rotate(30 65 25)" />
      <circle cx="50" cy="22" r="3" fill={secondaryColor}>
        {animated && (
          <animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" />
        )}
      </circle>
      
      {/* Hair */}
      <path d="M 25 30 Q 25 18 50 18 Q 75 18 75 30 Q 70 22 50 22 Q 30 22 25 30" 
            fill="#8b4513" />
      
      {/* Teddy Bear */}
      <ellipse cx="50" cy="82" rx="12" ry="10" fill="#d2691e" />
      <circle cx="45" cy="78" r="4" fill="#d2691e" />
      <circle cx="55" cy="78" r="4" fill="#d2691e" />
      <circle cx="42" cy="75" r="2" fill="#8b4513" />
      <circle cx="58" cy="75" r="2" fill="#8b4513" />
      <circle cx="50" cy="79" r="1" fill="#000" />
      <path d="M 48 81 Q 50 83 52 81" stroke="#000" strokeWidth="1" fill="none" />
      
      {animated && (
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 50 82;5 50 82;-5 50 82;0 50 82"
            dur="3s"
            repeatCount="indefinite"
            begin="0.5s"
          />
        </g>
      )}
    </svg>
  )
}

export default GenderIcon
